/**
 * Product Card Component
 *
 * Reusable product card for grids and listings
 * Task: T4.4.3 - Product Card component
 */

import { useNavigate } from 'react-router-dom';
import {
  Card,
  Box,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
} from '@shopify/polaris';
import { StarFilledIcon, HeartIcon } from '@shopify/polaris-icons';

export interface ProductCardData {
  id: string;
  name: string;
  slug: string;
  primaryImage: string | null;
  startingPrice: number | null;
  categoryName: string;
  categorySlug?: string;
  isHotSelling?: boolean;
  samplePrice?: number | null;
  stockStatus?: 'IN_STOCK' | 'OUT_OF_STOCK';
}

interface ProductCardProps {
  product: ProductCardData;
  onAddToWishlist?: (productId: string) => void;
  onQuickView?: (productId: string) => void;
  showWishlistButton?: boolean;
  showQuickView?: boolean;
}

export function ProductCard({
  product,
  onAddToWishlist,
  onQuickView,
  showWishlistButton = true,
  showQuickView = false,
}: ProductCardProps) {
  const navigate = useNavigate();

  const handleViewDetails = () => {
    navigate(`/products/${product.slug}`);
  };

  const handleWishlistClick = () => {
    onAddToWishlist?.(product.id);
  };

  const handleQuickViewClick = () => {
    onQuickView?.(product.id);
  };

  return (
    <Card>
      <div style={{ cursor: 'pointer' }} onClick={handleViewDetails}>
        <BlockStack gap="200">
          {/* Image Container */}
          <div style={{ position: 'relative' }}>
            <Box
              minHeight="180px"
              background="bg-surface-secondary"
              borderRadius="200"
              padding="200"
            >
              {product.primaryImage ? (
                <img
                  src={product.primaryImage}
                  alt={product.name}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '160px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                />
              ) : (
                <Box
                  minHeight="160px"
                  background="bg-surface-tertiary"
                  borderRadius="200"
                />
              )}
            </Box>

            {/* Hot Selling Badge */}
            {product.isHotSelling && (
              <div style={{ position: 'absolute', top: '8px', left: '8px' }}>
                <Badge tone="warning" icon={StarFilledIcon}>
                  Hot
                </Badge>
              </div>
            )}

            {/* Out-of-stock stays visible with a clear badge (bottom-left, so it
                never collides with the Hot badge on top). */}
            {product.stockStatus === 'OUT_OF_STOCK' && (
              <div style={{ position: 'absolute', bottom: '8px', left: '8px' }}>
                <Badge tone="attention">Out of Stock</Badge>
              </div>
            )}

            {/* Wishlist Button */}
            {showWishlistButton && onAddToWishlist && (
              <div
                style={{ position: 'absolute', top: '8px', right: '8px' }}
                onClick={(e) => { e.stopPropagation(); handleWishlistClick(); }}
              >
                <Button
                  icon={HeartIcon}
                  variant="tertiary"
                  accessibilityLabel="Add to wishlist"
                />
              </div>
            )}
          </div>

          {/* Product Info */}
          <Box padding="200">
            <BlockStack gap="100">
              <Text as="h3" variant="bodyMd" fontWeight="semibold" truncate>
                {product.name}
              </Text>

              <Text as="p" variant="bodySm" tone="subdued">
                {product.categoryName}
              </Text>

              {product.startingPrice && (
                <Text as="p" variant="bodyMd" fontWeight="bold">
                  Starting at ₹{product.startingPrice.toLocaleString()}
                </Text>
              )}

              {/* Action Buttons */}
              <Box paddingBlockStart="200">
                <InlineStack gap="200">
                  <Button size="slim" onClick={handleViewDetails}>
                    View Details
                  </Button>
                  {showQuickView && onQuickView && (
                    <div onClick={(e) => { e.stopPropagation(); handleQuickViewClick(); }}>
                      <Button size="slim" variant="secondary">
                        Quick View
                      </Button>
                    </div>
                  )}
                </InlineStack>
              </Box>
            </BlockStack>
          </Box>
        </BlockStack>
      </div>
    </Card>
  );
}
